import { Injectable } from '@angular/core';
import { Logger } from '@core/services';

export type ThemeType = 'light' | 'dark';

export enum Theme {
  Light = 'light',
  Dark = 'dark',
}

const log = new Logger('ThemeService');

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly _lightThemeColors: Record<string, string> = {
    // Colors
    '--light-teal': '#3dc19a',
    '--dark-teal': '#44a085',
    '--dark-blue': '#3e71bd',
    '--orange': '#e9380b',
    '--light-orange': '#ec5c37',
    '--dull-orange': '#dd5741',
    '--orange-opaque': 'rgb(233 56 11 / 12%)',
    '--teal-opaque': 'rgb(61 193 154 / 20%)',
    '--blue-opaque': 'rgb(62 113 189 / 20%)',
    '--yellow': '#fbc52d',
    '--dark-yellow': '#ebb106',
    '--deep-yellow': '#fec41a',
    '--yellow-opaque': 'rgb(235 177 6 / 14%)',
    '--slate-gray': '#667085',
    '--dull-gray': '#ffffff80',
    '--dark-charcoal': '#333843',
    '--deep-gray': '#393b3d',
    '--deep-slate-gray': '#323338',
    '--red': '#dc3545',
    '--green': '#389f6e',

    // Theme Colors
    '--surface-1': '#fafafa',
    '--text-color-light': '#ffffff',
    '--text-color-medium': '#667085',
    '--text-color-dark': '#333843',
    '--text-color-darker': '#000;',
    '--sidebar-icon-color': '#787878',
    '--icon-hover': '#667085',

    // Grayscale
    '--white': '#fff',
    '--white-rgb': '255, 255, 255',
    '--gray': '#e1e3e6',
    '--gray-50': '#f8f9fa',
    '--gray-100': '#f8f9fa',
    '--gray-150': '#f1f3f4',
    '--gray-200': '#e9ecef',
    '--gray-300': '#dee2e6',
    '--gray-400': '#ced4da',
    '--gray-500': '#adb5bd',
    '--gray-600': '#868e96',
    '--gray-700': '#495057',
    '--gray-800': '#343a40',
    '--gray-900': '#212529',
    '--black': '#000',
    '--black-rgb': '0, 0, 0',
    '--minor-gray': '#f2f2f5',

    // Light Theme Gradient & Shadow
    '--gradient-primary-light': 'linear-gradient(173deg, #e73609 -96.7%, #e9496e 17.35%, #ffc51a 111.15%)',
    '--gradient-primary-light-opaque': 'linear-gradient(173deg, rgb(231 54 9 / 60%) -96.7%, rgb(233 73 110 / 60%) 17.35%, rgb(255 197 26 / 60%) 111.15%)',
    '--shadow-light': '0 6px 19px 0 rgb(120 120 120 / 19%)',
    '--schedule-view-shadow': '0 2px 8px rgba(0 0 0 / 10%)',
    '--dialog-backdrop-color': 'rgba(40, 40, 40, 0.9)',
  };
  private readonly _darkThemeColors: Record<string, string> = {
    // Colors (brand and accents adapted for dark backgrounds)
    '--light-teal': '#3ca08b',
    '--dark-teal': '#3c9078',
    '--dark-blue': '#3a68ad',
    '--orange': '#e9380b', // Keeping brand orange
    '--light-orange': '#de5433', // Slightly adjusted for better contrast
    '--dull-orange': '#cc4b3a',
    '--orange-opaque': 'rgb(233 56 11 / 20%)', // Increased opacity for visibility on dark
    '--teal-opaque': 'rgb(61 193 154 / 25%)',
    '--blue-opaque': 'rgb(62 113 189 / 25%)',
    '--yellow': '#fbc52d', // Keeping brand yellow
    '--dark-yellow': '#d1a106',
    '--deep-yellow': '#eab61a',
    '--yellow-opaque': 'rgb(235 177 6 / 20%)', // Slightly higher opacity on dark
    '--slate-gray': '#ced4da',
    '--dull-gray': '#ffffff80', // More subtle transparency on dark
    '--dark-charcoal': '#ced4da',
    '--deep-gray': '#393b3d',
    '--deep-slate-gray': '#ced4da',
    '--red': '#c82333',
    '--green': '#2f865d',

    // Theme Colors
    '--surface-1': '#202124',
    '--text-color-light': '#f8f9fa', // Light text on dark
    '--text-color-medium': '#adb5bd', // Medium contrast text
    '--text-color-dark': '#dee2e6', // Lighter gray for darker text-tone roles
    '--text-color-darker': '#ffffff', // White text on dark
    '--sidebar-icon-color': '#787878', // Slightly lighter icon on dark bg
    '--icon-hover': '#ced4da',

    // Grayscale adjustments for dark theme
    '--white': '#212529',
    '--white-rgb': '33, 37, 41',
    '--gray': '#50525a', // Dark equivalent for #e1e3e6
    '--gray-50': '#212529',
    '--gray-100': '#343a40',
    '--gray-150': '#3c4043',
    '--gray-200': '#495057',
    '--gray-300': '#868e96',
    '--gray-400': '#adb5bd',
    '--gray-500': '#ced4da',
    '--gray-600': '#dee2e6',
    '--gray-700': '#e9ecef',
    '--gray-800': '#f8f9fa',
    '--gray-900': '#ffffff',
    '--black': '#f8f9fa',
    '--black-rgb': '248, 249, 250',
    '--minor-gray': '#2a2a2d',

    // Gradient & Shadow for Dark Theme
    // A darker, more muted version of the light gradient
    // '--gradient-primary-light': 'linear-gradient(173deg, #7f2c05 -96.7%, #9b3f57 17.35%, #b28f17 111.15%)',
    // '--gradient-primary-light-opaque': 'linear-gradient(173deg, rgb(127 44 5 / 60%) -96.7%, rgb(155 63 87 / 60%) 17.35%, rgb(178 143 23 / 60%) 111.15%)',
    '--shadow-light': '0 6px 19px 0 rgb(0 0 0 / 50%)', // Dark shadow for dark theme
    '--schedule-view-shadow': '0 2px 4px rgba(255 255 255 / 10%)', // Darker shadow for schedule view
    '--dialog-backdrop-color': 'rgba(0,0,0,0.8)', // Darker backdrop for dialogs
  };
  private _currentTheme: ThemeType = Theme.Light;

  constructor() {
    log.info('Theme service initialized');

    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      this._currentTheme = savedTheme as ThemeType;
    } else {
      const systemSettingDark = window.matchMedia('(prefers-color-scheme: dark)');

      if (systemSettingDark.matches) {
        this._currentTheme = Theme.Dark;
      } else {
        this._currentTheme = Theme.Light;
      }

      localStorage.setItem('theme', this._currentTheme);
    }

    this.autoSetTheme();
  }

  get currentTheme(): ThemeType {
    return this._currentTheme;
  }

  set currentTheme(theme: ThemeType) {
    this._currentTheme = theme;
    localStorage.setItem('theme', theme);
    this.autoSetTheme();
  }

  setLightTheme(): void {
    this._currentTheme = Theme.Light;
    this._setTheme(this._lightThemeColors);
    log.info('Light theme set');
  }

  setDarkTheme(): void {
    this._currentTheme = Theme.Dark;
    this._setTheme(this._darkThemeColors);
    log.info('Dark theme set');
  }

  autoSetTheme(): void {
    switch (this._currentTheme) {
      case Theme.Light:
        this.setLightTheme();
        break;
      case Theme.Dark:
        this.setDarkTheme();
        break;
      default:
        this.setLightTheme();
        break;
    }
  }

  private _setTheme(colors: Record<string, string>): void {
    const root = document.documentElement;
    Object.keys(colors).forEach((key) => {
      root.style.setProperty(key, colors[key]);
    });
  }
}
